import { CustomilyUser, ReferrerData, StoreList } from '@/typings/user';
import api, { toUrlEncoded } from './baseApi';
import { ApiKeysDto, EmailConfirmationDto, EmailConfirmedDto, UserEmailDto } from './typings/user';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const OpenAIApi = require('openai');

export const logout = async (): Promise<void> => {
    return await api.get('Users/Logout');
};

export const passwordCheck = async (
    password: string
): Promise<{
    Valid: boolean;
}> => {
    const response = await api.post(
        'Users/PasswordCheck',
        toUrlEncoded({
            Password: password
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const getUser = async (): Promise<CustomilyUser> => {
    const response = await api.get('Users/GetMe');
    return response.data;
};

export const getStores = async (from: number, to: number, search?: string): Promise<StoreList> => {
    const response = await api.get('Users/Stores', {
        params: {
            from,
            to,
            search: search?.toLocaleLowerCase()
        }
    });
    return response.data;
};

export const getUsers = async (): Promise<CustomilyUser[]> => {
    const response = await api.get('Users');
    return response.data;
};

export const getEmail = async (): Promise<UserEmailDto> => {
    const response = await api.get('Users/Email');
    return response.data;
};

export const getApiKeys = async (password: string): Promise<ApiKeysDto[]> => {
    const response = await api.post('Users/ApiKey/Get', {
        password
    });
    return response.data;
};

export const generateApiKey = async (password?: string): Promise<ApiKeysDto> => {
    const response = await api.post('Users/ApiKey/Generate', {
        password
    });
    return response.data;
};

export const updateEmail = async (password: string, newEmail: string): Promise<void> => {
    const response = await api.post(
        'Users/Email',
        toUrlEncoded({
            Email: newEmail,
            Password: password
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const updateUser = async (
    name?: string | undefined,
    librarySegmentation?: boolean | undefined
): Promise<CustomilyUser> => {
    const response = await api.put(
        'Users',
        toUrlEncoded({
            Name: name,
            SegmentLibraryImages: librarySegmentation
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const updateUserAffiliate = async (PaypalEmail?: string | undefined): Promise<CustomilyUser> => {
    const response = await api.put(
        'Users/Affiliate',
        toUrlEncoded({
            PaypalEmail
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const getEmailConfirmation = async (): Promise<EmailConfirmationDto> => {
    const response = await api.get('Users/Email/Confirmation');
    return response.data;
};

export const confirmEmail = async (username: string, token: string): Promise<EmailConfirmedDto> => {
    const response = await api.post(
        'Users/Email/Confirmation',
        toUrlEncoded({
            Username: username,
            Token: token
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const changePassword = async (
    currentPassword: string,
    newPassword: string,
    confirmNewPassword: string
): Promise<void> => {
    const response = await api.post(
        'Users/Password',
        toUrlEncoded({
            CurrentPassword: currentPassword,
            NewPassword: newPassword,
            ConfirmationPassword: confirmNewPassword
        }),
        {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }
    );
    return response.data;
};

export const hasIntegrationAccess = async (): Promise<boolean> => {
    const { data } = await api.get('Users/HasIntegrationAccess');
    return data.result;
};

export const setRemoveBgApiKey = async (apiKey: string) => {
    const response = await api.post('Users/RemoveBg', null, {
        params: {
            key: apiKey
        }
    });
    return response.data;
};

export const setOpenAiApiKey = async (key: string) => {
    try {
        if (key) {
            const openai = new OpenAIApi({
                apiKey: key,
                dangerouslyAllowBrowser: true
            });
            await openai.models.list();
        }

        await api.post('Users/OpenAi', null, {
            params: { key }
        });
    } catch (error) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        throw (error as any).error;
    }
};

export const setWilcomCredentials = async (wilcomAppId: string, wilcomAppKey: string) => {
    const response = await api.post('Users/WilcomCredentials', null, {
        params: {
            wilcomAppId: wilcomAppId,
            wilcomAppKey: wilcomAppKey
        }
    });
    return response.data;
};

export const setMelcoCredentials = async (melcoUsername: string, melcoApiKey: string) => {
    const response = await api.post('Users/MelcoCredentials', null, {
        params: {
            melcoUsername: melcoUsername,
            melcoApiKey: melcoApiKey
        }
    });
    return response.data;
};

export const setSpotifyCredentials = async (spotifyClientID: string, spotifyClientSecret: string) => {
    const response = await api.post('Users/SpotifyCredentials', null, {
        params: {
            spotifyClientID: spotifyClientID,
            spotifyClientSecret: spotifyClientSecret
        }
    });
    return response.data;
};

export const deleteCanvaCredentials = async () => {
    const response = await api.delete('Users/CanvaCredentials');
    return response.data;
};

export const setCutoutProApiKey = async (apiKey: string) => {
    const response = await api.post('Users/CutoutPro', null, {
        params: {
            key: apiKey
        }
    });
    return response.data;
};

export const setGoogleGeminiApiKey = async (apiKey: string) => {
    const response = await api.post('Users/GoogleGemini', null, {
        params: {
            key: apiKey
        }
    });
    return response.data;
};

export const setOpenRouterApiKey = async (apiKey: string) => {
    const response = await api.post('Users/OpenRouter', null, {
        params: {
            key: apiKey
        }
    });
    return response.data;
};

export const setPicWishApiKey = async (apiKey: string) => {
    const response = await api.post('Users/Picwish', null, {
        params: {
            key: apiKey
        }
    });
    return response.data;
};

export interface RateLimitDto {
    provider: string;
    attemptsPerIp: number | null;
    retryAfterMinutes: number | null;
}

export const getRateLimits = async (): Promise<RateLimitDto[]> => {
    const response = await api.get('Users/RateLimits');
    return response.data;
};

export const saveRateLimit = async (payload: RateLimitDto): Promise<void> => {
    await api.post('Users/RateLimits', payload);
};

export const setReferrer = async (referrer: string) => {
    const response = await api.post('Users/Referrer', null, {
        params: {
            referrer: referrer
        }
    });
    return response.data;
};

export const getReferrerData = async (): Promise<ReferrerData> => {
    const response = await api.get('Users/Referrer');
    return response.data;
};

export const createTemplate = async (shopifyProductId: string) => {
    const response = await api.post('Users/CreateTemplate/Shopify', null, {
        params: {
            productId: shopifyProductId
        }
    });
    return response.data;
};
